/**
 * useScrollAnime — fire an anime.js animation when an element scrolls
 * into view. Uses IntersectionObserver under the hood so we get clean
 * threshold / rootMargin / once semantics without a polyfill.
 *
 * Most landing-page reveals should fire ONCE: scroll past, animate in,
 * never replay. Pass `once: false` for the rare case (e.g. a sticky
 * stat that re-animates each time it re-enters).
 *
 * Example:
 *   const ref = useScrollAnime<HTMLDivElement>({
 *     opacity: [0, 1],
 *     translateY: [24, 0],
 *     duration: DURATION.base,
 *     ease: EASE.soft,
 *   });
 *   return <div ref={ref}>...</div>;
 */

import { useEffect, useRef } from "react";
import { animate, type AnimationParams, type JSAnimation } from "animejs";
import { prefersReducedMotion } from "./prefersReducedMotion";

export interface UseScrollAnimeOptions extends AnimationParams {
  /** IntersectionObserver threshold (0-1). Default 0.2. */
  threshold?: number;
  /** Fire once and then stop observing. Default true. */
  once?: boolean;
  /** rootMargin passed to IntersectionObserver. Default "0px 0px -10% 0px". */
  rootMargin?: string;
}

/**
 * Mirrors useAnime's snap-to-final logic so reduced-motion users still
 * see the post-animation layout instead of the pre-animation state.
 */
function snapToFinal(el: HTMLElement, params: AnimationParams): void {
  const pickEnd = (v: unknown): string | number | null => {
    if (Array.isArray(v) && v.length > 0) {
      const last = v[v.length - 1];
      if (typeof last === "number" || typeof last === "string") return last;
    }
    if (typeof v === "number" || typeof v === "string") return v;
    return null;
  };

  const p = params as Record<string, unknown>;

  const opacity = pickEnd(p.opacity);
  if (opacity !== null) el.style.opacity = String(opacity);

  // Box-model dimensions (used by progress bars / dividers)
  const width = pickEnd(p.width);
  if (width !== null) {
    el.style.width = typeof width === "number" ? `${width}px` : width;
  }
  const height = pickEnd(p.height);
  if (height !== null) {
    el.style.height = typeof height === "number" ? `${height}px` : height;
  }

  // SVG path-draw shorthand
  const sdo = pickEnd(p.strokeDashoffset);
  if (sdo !== null) {
    el.style.strokeDashoffset = typeof sdo === "number" ? String(sdo) : sdo;
  }

  // Transform components — combined into a single transform string
  const tx = pickEnd(p.translateX);
  const ty = pickEnd(p.translateY);
  const sc = pickEnd(p.scale);
  const sx = pickEnd(p.scaleX);
  const sy = pickEnd(p.scaleY);
  const rot = pickEnd(p.rotate);

  if (tx !== null || ty !== null || sc !== null || sx !== null || sy !== null || rot !== null) {
    const parts: string[] = [];
    if (tx !== null) parts.push(`translateX(${typeof tx === "number" ? `${tx}px` : tx})`);
    if (ty !== null) parts.push(`translateY(${typeof ty === "number" ? `${ty}px` : ty})`);
    if (sc !== null) parts.push(`scale(${sc})`);
    if (sx !== null) parts.push(`scaleX(${sx})`);
    if (sy !== null) parts.push(`scaleY(${sy})`);
    if (rot !== null) parts.push(`rotate(${typeof rot === "number" ? `${rot}deg` : rot})`);
    el.style.transform = parts.join(" ");
  }
}

export function useScrollAnime<T extends HTMLElement = HTMLElement>(
  config: UseScrollAnimeOptions,
): React.RefObject<T> {
  const ref = useRef<T>(null);
  // Latest config in a ref so we don't recreate the observer every render.
  const configRef = useRef<UseScrollAnimeOptions>(config);
  configRef.current = config;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const {
      threshold = 0.2,
      once = true,
      rootMargin = "0px 0px -10% 0px",
      ...params
    } = configRef.current;

    if (prefersReducedMotion()) {
      snapToFinal(el, params);
      return;
    }

    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      // No IO available (very old browsers / SSR) — just play immediately.
      animate(el, params);
      return;
    }

    let active: JSAnimation | null = null;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            active?.pause();
            active = animate(el, params);
            if (once) {
              observer.disconnect();
              return;
            }
          }
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      active?.pause();
      active = null;
    };
  }, []);

  return ref;
}
